import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import AssignmentTurnedInOutlinedIcon from '@material-ui/icons/AssignmentTurnedInOutlined';
import { RMSCommonDialog } from '../../../shared/components/dialog';
import SearchPersonAutoComplete from '../../../shared/components/modules/SearchPersonAutoComplete';
import {
  RMSSelect,
  RMSDatePicker,
  RMSTimePicker,
  RMSTextField,
} from '../../../shared/components/form-controls';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import checkOutRequestSchema from '../../schema/checkOutRequestSchema';
import { notify } from 'reducers/NotifierReducer';
import { getFirstErrorMessage } from '../../../shared/utility/helper';
import useEvidenceDictionaryOptions from '../../hooks/useEvidenceDictionaryOptions';
import { CODE_DESCRIPTION_SELECT_PROPS } from '../../../shared/utility/selectOptionRenderers';
import FieldIdMismatchWarning from '../common/FieldIdMismatchWarning';
import AddPersonDialog from '../../../user/components/common/AddPersonDialog';
import BulkEvidenceSummary from './BulkEvidenceSummary';

const CheckOutRequestDialog = ({
  open,
  initialData,
  ptsEvidences = [],
  loading,
  onSubmit,
  onClose,
}) => {
  const dispatch = useDispatch();
  const {
    checkoutLocationOptions: pickupLocationOptions,
    checkoutReasonOptions: reasonOptions,
  } = useEvidenceDictionaryOptions();
  const [addPersonDialogOpen, setAddPersonDialogOpen] = useState(false);

  const isBulk = ptsEvidences.length > 0;

  const evidenceIdList = useMemo(() => ptsEvidences.map((ref) => ref.ptsEvidenceID), [
    ptsEvidences,
  ]);

  const methods = useForm({
    resolver: CreateFormResolver(checkOutRequestSchema),
    defaultValues: {
      requestedBy: initialData?.RequestedByPerson?.FullName || '',
      requestedByObject: initialData?.RequestedByPerson?.ptsPersonID || null,
      pickupLocation: initialData?.PickupLocationID || '',
      reason: initialData?.ReasonNeededID || '',
      pickupDate: initialData?.PickupDate || null,
      pickupTime: initialData?.PickupTime || null,
      comments: initialData?.Comments || '',
    },
  });

  const {
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { isDirty },
  } = methods;

  const [requestedBy, requestedByObject] = useWatch({
    control,
    name: ['requestedBy', 'requestedByObject'],
  });

  const isRequestedByIdMissing = !!requestedBy && !requestedByObject;

  const handleSelectedPerson = useCallback(
    (person) => {
      setValue('requestedByObject', person?.ptsPersonID || null, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue('requestedBy', person?.FullName || '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const handleSave = (data) => {
    onSubmit?.({
      ...data,
      ptsEvidenceIDs: evidenceIdList,
      evidenceRefs: ptsEvidences,
    });
    onClose?.();
  };

  const handleRequestSubmit = handleSubmit(handleSave, (errors) =>
    dispatch(notify(getFirstErrorMessage(errors), 'error'))
  );

  useEffect(() => {
    reset({
      requestedBy: initialData?.RequestedByPerson?.FullName || '',
      requestedByObject: initialData?.RequestedByPerson?.ptsPersonID || null,
      pickupLocation: initialData?.PickupLocationID || '',
      reason: initialData?.ReasonNeededID || '',
      pickupDate: initialData?.PickupDate || null,
      pickupTime: initialData?.PickupTime || null,
      comments: initialData?.Comments || '',
    });
  }, [initialData, reset]);

  return (
    <FormProvider {...methods}>
      <RMSCommonDialog
        open={open}
        loading={loading}
        onClose={onClose}
        onSave={handleRequestSubmit}
        maxWidth="sm"
        saveDisabled={!isDirty}
        fullWidth
        title={
          <Box>
            <Box display="flex" alignItems="center">
              <AssignmentTurnedInOutlinedIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">
                {isBulk
                  ? 'New Check Out Request'
                  : initialData
                  ? 'Edit Check Out Request'
                  : 'New Check Out Request'}
              </Typography>
            </Box>
          </Box>
        }
        saveButton={{
          show: true,
          type: 'simple',
          icon: <AssignmentTurnedInOutlinedIcon />,
          label: initialData ? 'Save Changes' : 'Submit Request',
        }}
        cancelButton={{
          show: true,
          type: 'simple',
          variant: 'outlined',
          label: 'Cancel',
        }}>
        {isBulk && ptsEvidences.length > 0 && (
          <BulkEvidenceSummary items={ptsEvidences} actionLabel="requested" />
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SearchPersonAutoComplete
              name="requestedByObject"
              label="Requested By"
              placeholder="Search officer..."
              meButtonVisible
              defaultPersonId={initialData?.RequestedByPerson?.ptsPersonID}
              defaultPersonSelectedCallback={handleSelectedPerson}
              onSelectionChange={handleSelectedPerson}
              onAddIconClick={() => setAddPersonDialogOpen(true)}
              formatLabel={(person) =>
                [person.FirstName, person.MiddleName, person.LastName].filter(Boolean).join(' ')
              }
            />
            <FieldIdMismatchWarning show={isRequestedByIdMissing} label={requestedBy} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <RMSSelect
                name="pickupLocation"
                label="Pickup Location"
                options={pickupLocationOptions}
                {...CODE_DESCRIPTION_SELECT_PROPS}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <RMSSelect
              name="reason"
              label="Reason Needed"
              options={reasonOptions}
              {...CODE_DESCRIPTION_SELECT_PROPS}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <RMSDatePicker name="pickupDate" label="Pickup Date" />
          </Grid>

          <Grid item xs={12} md={4}>
            <RMSTimePicker name="pickupTime" label="Pickup Time" />
          </Grid>

          <Grid item xs={12}>
            <RMSTextField
              name="comments"
              label="Comments"
              multiline
              rows={4}
              placeholder="Optional notes..."
            />
          </Grid>
        </Grid>
      </RMSCommonDialog>

      {addPersonDialogOpen && (
        <AddPersonDialog
          open={addPersonDialogOpen}
          onClose={() => setAddPersonDialogOpen(false)}
          onPersonCreated={handleSelectedPerson}
        />
      )}
    </FormProvider>
  );
};

export default CheckOutRequestDialog;
